import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { can, intakeNeedsSerial } from '@kronos/domain'
import type { Role } from '@kronos/domain'
import { repository } from '../data'
import type { BoardOrder } from '../data'
import { formatDay, daysUntil } from './dates'
import { BuildProgress } from './BuildProgress'
import { Badge } from './primitives'
import type { Tone } from './primitives'
import { navigate } from './router'
import { Link } from './shell/Link'

/**
 * One build order.
 *
 * The order of things on the card is the order somebody reads them in: what it
 * is, when it is wanted, how many can be started *now*, and — only if that
 * number is zero — what is in the way.
 */
export function BuildOrderCard({
  order,
  roles,
}: {
  order: BoardOrder
  roles: readonly Role[]
}): JSX.Element {
  const queryClient = useQueryClient()
  const [problem, setProblem] = useState('')
  const [starting, setStarting] = useState(false)

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['build-orders'] })
    void queryClient.invalidateQueries({ queryKey: ['shortages'] })
  }
  const fail = (e: Error): void => setProblem(e.message.replace(/^\d+:\s*/, ''))

  const release = useMutation({
    mutationFn: () => repository.releaseBuildOrder(order.id),
    onSuccess: () => {
      setProblem('')
      refresh()
    },
    onError: fail,
  })

  const days = daysUntil(order.needBy)
  const state = STATE[order.state] ?? { label: order.state, tone: 'neutral' as const }

  return (
    <div className="panel panel--padded order-card">
      <div className="order-card__head">
        <span className="stack" style={{ gap: 2, minWidth: 0 }}>
          <span className="order-card__title">
            {order.quantity} × {order.typeName} <span className="muted">{order.revisionName}</span>
          </span>
          <span className="muted order-card__meta">
            Due {formatDay(order.needBy)}
            {order.state !== 'complete' ? ` · ${days < 0 ? `${-days} days late` : `in ${days} days`}` : ''}
            {order.note ? ` · ${order.note}` : ''}
          </span>
        </span>
        <Badge tone={state.tone}>{state.label}</Badge>
      </div>

      {order.state === 'draft' ? (
        <Draft order={order} roles={roles} onRelease={() => release.mutate()} pending={release.isPending} />
      ) : (
        <>
          <BuildProgress order={order} />
          {order.shortOf.length > 0 ? (
            <span className="warn order-card__meta">
              Waiting on {order.shortOf.map((s) => `${s.need - s.have} × ${s.typeName}`).join(', ')}
            </span>
          ) : null}
        </>
      )}

      {order.running.length > 0 ? (
        <div className="row-list order-card__running">
          {order.running.map((run) => (
            <Link key={run.executionId} to={{ name: 'execution', id: run.executionId }} className="row-item">
              <span className="row-item__main">
                <span className="row-item__title">
                  On the bench · <span className="serial">{run.serial}</span>
                </span>
              </span>
              <span className="row-item__side">
                {run.answered} of {run.steps} steps →
              </span>
            </Link>
          ))}
        </div>
      ) : null}

      {order.state === 'released' && can(roles, 'open_execution') && order.remaining > 0 ? (
        starting ? (
          <StartUnit order={order} onDone={() => { setStarting(false); refresh() }} onCancel={() => setStarting(false)} />
        ) : (
          <button className="primary" style={{ alignSelf: 'flex-start' }} onClick={() => setStarting(true)}>
            Start one{order.ready > 0 ? ` · ${order.ready} ready` : ''}
          </button>
        )
      ) : null}

      {problem ? <p className="critical" role="alert">{problem}</p> : null}
    </div>
  )
}

const STATE: Readonly<Record<string, { label: string; tone: Tone }>> = {
  draft: { label: 'Draft', tone: 'warning' },
  released: { label: 'On the floor', tone: 'info' },
  complete: { label: 'Complete', tone: 'healthy' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
}

function Draft({
  order,
  roles,
  onRelease,
  pending,
}: {
  order: BoardOrder
  roles: readonly Role[]
  onRelease: () => void
  pending: boolean
}): JSX.Element {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <span className="muted">
        A draft puts nothing on the floor or the buy list until it’s released.
      </span>
      {!order.hasBuildProcedure ? (
        <span className="warn">
          {order.revisionName} has no build steps written yet, so it can’t go to the floor.
        </span>
      ) : null}
      {can(roles, 'release_build_order') ? (
        <button
          className="primary"
          // Disabled rather than left to fail: the sentence above already says
          // why, and a button that answers a tap with a refusal it could have
          // predicted is a button that teaches people not to trust it.
          disabled={pending || !order.hasBuildProcedure}
          onClick={onRelease}
        >
          Release to the floor
        </button>
      ) : (
        <span className="muted">An engineer releases it.</span>
      )}
    </div>
  )
}

/** Ready · blocked · under way · built, and what the blocked ones want. */
function StartUnit({
  order,
  onDone,
  onCancel,
}: {
  order: BoardOrder
  onDone: () => void
  onCancel: () => void
}): JSX.Element {
  const [serial, setSerial] = useState('')
  const [problem, setProblem] = useState('')

  /*
   * Whether there is a label to scan at all (§10.1).
   *
   * An assembly is reached by scanning any part inside it and walking up the
   * tree, so it carries no label and its serial is issued by the server.
   * Putting a viewfinder in front of somebody here would be the app asking for
   * something the part cannot give — the same reasoning as `receiveUnmarked`
   * at intake.
   */
  const needsLabel = intakeNeedsSerial(order.marking)

  const start = useMutation({
    mutationFn: () =>
      needsLabel
        ? repository.startBuildUnit(order.id, serial.trim().toUpperCase())
        : repository.startBuildUnit(order.id),
    onSuccess: (result) => {
      onDone()
      navigate({ name: 'execution', id: result.executionId })
    },
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })

  return (
    <div className="stack" style={{ gap: 6 }}>
      {needsLabel ? (
        <>
          <span className="muted">
            Scan or type a blank label for the {order.typeName} being built.
          </span>
          <input
            className="serial"
            autoFocus
            value={serial}
            placeholder="K7F38"
            onChange={(e) => setSerial(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && serial.trim() !== '') start.mutate()
            }}
          />
        </>
      ) : (
        <span className="muted">
          A {order.typeName} carries no label — Kronos issues its serial, and you find it later by
          scanning any part inside it. Nothing to peel or stick.
        </span>
      )}
      {order.ready === 0 ? (
        <span className="muted">
          The shelf says there are not enough parts for this one. Starting anyway is allowed — the
          build will stop at the step whose part is genuinely missing.
        </span>
      ) : null}
      {problem ? <p className="critical" role="alert">{problem}</p> : null}
      <div className="row" style={{ gap: 8 }}>
        <button
          className="primary"
          disabled={(needsLabel && serial.trim() === '') || start.isPending}
          onClick={() => start.mutate()}
        >
          Start building
        </button>
        <button onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}
